import { Avatar, Button, Combobox, Tag, TextField } from '@lego/connect-components-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { KnownOperator } from './types';
import { initials, isValidEmail, matchesOperator, operatorDisplayName } from './utils';

export const AssignOperatorSearch = ({
  assignableOperators,
  isBusy,
  onAssignKnownOperator,
  onInviteAndAssign,
}: {
  assignableOperators: KnownOperator[];
  isBusy: boolean;
  onAssignKnownOperator: (id: number) => Promise<boolean>;
  onInviteAndAssign: (email: string) => Promise<boolean>;
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const trimmed = query.trim();
  const suggestions = assignableOperators.filter((op) => matchesOperator(op, trimmed)).slice(0, 8);
  const showInvite =
    isValidEmail(trimmed) &&
    !assignableOperators.some((op) =>
      [op.email, op.displayName].some((name) => name?.toLowerCase() === trimmed.toLowerCase())
    );
  const inviteValue = `invite:${trimmed}`;

  const handleAssignOption = async (value: string) => {
    if (isBusy) return;
    const success =
      value === inviteValue && showInvite
        ? await onInviteAndAssign(trimmed)
        : await onAssignKnownOperator(Number(value));
    if (success) {
      setQuery('');
      setResetKey((previous) => previous + 1);
    }
  };

  return (
    <div className="border-t border-core-slate-100 pt-300 space-y-100">
      <h3 className="text-body-sm font-bold text-core-slate-1100">
        {t('operatorAccessPage.display.assignToGroup')}
      </h3>
      <div className="flex items-center gap-100">
        <div className="flex-1 min-w-0">
          {assignableOperators.length === 0 ? (
            <TextField
              size="medium"
              value={query}
              onChange={(_event, value) => setQuery(value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && showInvite) void handleAssignOption(inviteValue);
              }}
              placeholder={t('operatorAccessPage.display.searchOrEmail')}
            />
          ) : (
            <Combobox
              key={resetKey}
              size="medium"
              fullWidth
              placeholder={t('operatorAccessPage.display.searchOrEmail')}
              onSearch={setQuery}
              onChange={(_event, [selectedOption]) => {
                if (selectedOption) void handleAssignOption(selectedOption.value);
              }}
              filterFunction={(item, search) =>
                item.value.startsWith('invite:') ||
                assignableOperators.some(
                  (op) => String(op.id) === item.value && matchesOperator(op, search)
                )
              }
            >
              <>
                {suggestions.map((op) => {
                  const displayName = operatorDisplayName(op);
                  return (
                    <Combobox.Option
                      key={op.id}
                      value={String(op.id)}
                      subtext={op.displayName ? (op.email ?? undefined) : undefined}
                    >
                      <span className="flex items-center gap-100">
                        <Avatar
                          type="initials"
                          name={displayName}
                          initials={initials(displayName)}
                          size="small"
                        />
                        {displayName}
                        {!op.oid && (
                          <Tag variant="informative" size="small">
                            {t('operatorAccessPage.display.invitedTag')}
                          </Tag>
                        )}
                      </span>
                    </Combobox.Option>
                  );
                })}
                {showInvite && (
                  <Combobox.Option value={inviteValue}>
                    {t('operatorAccessPage.action.inviteAndAdd')}: {trimmed}
                  </Combobox.Option>
                )}
              </>
            </Combobox>
          )}
        </div>
        <Button
          variant="primary"
          size="small"
          disabled={isBusy || !trimmed || (!showInvite && suggestions.length === 0)}
          onClick={() =>
            void handleAssignOption(showInvite ? inviteValue : String(suggestions[0]?.id))
          }
        >
          {t(
            showInvite ? 'operatorAccessPage.action.inviteAndAdd' : 'operatorAccessPage.action.add'
          )}
        </Button>
      </div>
      <p className="text-body-xs text-core-slate-900">
        {t(
          showInvite
            ? 'operatorAccessPage.display.inviteByEmailHelp'
            : 'operatorAccessPage.display.searchOrEmailHelp'
        )}
      </p>
    </div>
  );
};
